import Axios from 'api/client';
import {
    Integration,
    IntegrationMapping,
    IntegrationMappingContext,
    IntegrationSetting,
} from 'types/domain';

interface IntegrationBaseArgs {
    name: string;
    settings?: Partial<IntegrationSetting>;
    active?: boolean;
}

interface GenericIntegrationArgs extends IntegrationBaseArgs {
    key: string;
}

interface ThankViewIntegrationArgs extends IntegrationBaseArgs {
    name: 'thankview';
    client_id: string;
    client_secret: string;
}

interface DoubleTheDonationIntegrationArgs extends IntegrationBaseArgs {
    name: 'doublethedonation';
    public_key: string;
    private_key: string;
}

interface MailChimpIntegrationArgs extends IntegrationBaseArgs {
    name: 'mailchimp';
    api_key: string;
}

type IntegrationArgs =
    | IntegrationBaseArgs
    | GenericIntegrationArgs
    | ThankViewIntegrationArgs
    | DoubleTheDonationIntegrationArgs
    | MailChimpIntegrationArgs;

interface IntegrationMappingArgs {
    id: number;
    context: string;
    settings: string;
    type: string;
}

const fetchIntegrations = (accountId: number) =>
    Axios.get<Integration[]>(`/accounts/${accountId}/integrations`).then(response => response.data);

const createIntegration = (accountId: number, params: IntegrationArgs) =>
    Axios.post<Integration>(`/accounts/${accountId}/integrations`, params).then(
        response => response.data,
    );

const updateIntegration = (
    accountId: number,
    integrationId: number,
    params: Partial<IntegrationArgs>,
) =>
    Axios.patch<Integration>(`/accounts/${accountId}/integrations/${integrationId}`, params).then(
        response => response.data,
    );

const triggerIntegrationActions = (
    accountId: number,
    integrationId: number,
    actions: string[],
    campaignId?: number,
) => {
    const searchParams = new URLSearchParams({ actions: actions.toString() });
    if (campaignId) searchParams.set('campaign_id', campaignId.toString());

    return Axios.post(
        `/accounts/${accountId}/integrations/${integrationId}?${searchParams.toString()}`,
    ).then(response => response.data);
};

const fetchConnectionMappings = (accountId: number, type: IntegrationMappingContext) =>
    Axios.get<IntegrationMapping[]>(`/accounts/${accountId}/connection_mappings?type=${type}`).then(
        response => response.data,
    );

const createConnectionMapping = (args: IntegrationMappingArgs) =>
    Axios.post<IntegrationMapping>('/connection_mappings', args).then(response => response.data);

const updateConnectionMapping = (mappingId: string, args: IntegrationMappingArgs) =>
    Axios.patch<IntegrationMapping>(`/connection_mappings/${mappingId}`, args).then(
        response => response.data,
    );

export {
    fetchIntegrations,
    createIntegration,
    updateIntegration,
    triggerIntegrationActions,
    fetchConnectionMappings,
    createConnectionMapping,
    updateConnectionMapping,
};
export type {
    IntegrationArgs,
    IntegrationBaseArgs,
    GenericIntegrationArgs,
    ThankViewIntegrationArgs,
    DoubleTheDonationIntegrationArgs,
    MailChimpIntegrationArgs,
    IntegrationMappingArgs,
};
